import { useState } from 'react';
import type { ColumnType } from 'antd/es/table';
import styles from './MeterReadDetailsTable.module.css';
import MeterReadDetailsTableRunningTotalCell from './MeterReadDetailsTableRunningTotalCell';
import MeterReadDetailsTable from './MeterReadDetailsTable';

/** minimal types so TS is happy */
type Cell = {
  month_year: string;
  value: number | string | null;
  meter_computed_id?: number;
  readonly?: boolean;
};
type Row = { field: string; label: string; months: Cell[]; editable?: boolean; };

const NETGEN_PARENT = 'prod_stat_netgen';
const NETGEN_CHILDREN = new Set(['Prod_Stat_Adjustments_kWh', 'units_delivered', 'units_consumed']);
const NETGEN_CHILDREN_ORDER = ['Prod_Stat_Adjustments_kWh', 'units_delivered', 'units_consumed'];
const SEPARATOR_FIELD = 'Prod_Stat_Adjustments_kWh';
const AGC_FIELDS = new Set(['agc_energy_kwh', 'agc_cost']);
// Desired order for rows after the section separator (post_prod_stat_netgen group)
const POST_SECTION_ORDER = [
  'post_prod_stat_netgen',
  'post_prod_stat_meter_read_type',
  'agc_energy_kwh',
  'rate',
  'prod_stat_cost_original',
  'dollar_value',
  'agc_cost',
  'invoice_cost',
];

const ContractTitle = ({ contractStart, contractEnd }: { contractStart: Date | null; contractEnd: Date | null }) => {
  // If both dates are missing, show N/A
  if (!contractStart && !contractEnd) {
    return (
      <>
        Contract
        <br />
        Total
        <br />
        N/A
      </>
    );
  }

  // Use UTC accessors: these dates are parsed from ISO strings (UTC midnight) and
  // .getMonth()/.getFullYear() would shift one month back in negative-UTC-offset timezones.
  const startStr = contractStart
    ? `${contractStart.getUTCMonth() + 1}-${contractStart.getUTCFullYear()}`
    : 'N/A';
  const endStr = contractEnd
    ? `${contractEnd.getUTCMonth() + 1}-${contractEnd.getUTCFullYear()}`
    : 'N/A';

  return (
    <>
      Contract
      <br />
      Total
      <br />
      {`${startStr} to ${endStr}`}
    </>
  );
};

const MeterReadDetailsMainTable = ({
  id,
  rows,
  handleChange,
  saving,
  updating,
  start,
  end,
  shouldUseContractView,
  energyBid,
  isEditable,
  meterType,
}: {
  id: string | undefined;
  rows: Row[];
  handleChange: any;
  saving: any;
  updating: any;
  start?: Date;
  end?: Date;
  shouldUseContractView?: boolean;
  energyBid?: number | null;
  isEditable?: boolean;
  meterType?: string | null;
}) => {
  const [netgenCollapsed, setNetgenCollapsed] = useState(true);

  const toggleNetgen = () => setNetgenCollapsed((prev) => !prev);

  const fieldsWithRunningTotals = [
    'Prod Stat Net Generation - kWh',
    'AGC Energy (kWh)',
    'AGC Cost',
    'kWh Generated',
    'kWh Used',
    'Invoice Net Gen (kWh)',
    'Manual kWh Adjustment (to the net)',
    'Generation Cost',
    'SPP Cost',
    'Invoice Cost',
    'Actual Net',
    'Prod Stat Adjustments - kWh',
  ];

  // Parse date strings (YYYY-MM-DD) directly to avoid timezone shifts
  // Extract year/month and create Date in local timezone
  const parseDate = (dateStr: string | Date | null | undefined) => {
    if (!dateStr) return null;
    if (dateStr instanceof Date) return dateStr;
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day || 1);
  };

  const contractStart = parseDate(start);
  const contractEnd = parseDate(end);

  /** Column widths chosen so sum > 1200px -> horizontal scrollbar appears */
  const RUN_TOT_W = 150;
  const CONTR_W = 150;
  const BID_W = 150;

  const totalsCols: ColumnType<Row>[] = [
    {
      title: 'Running Total',
      dataIndex: 'runningTotal',
      width: RUN_TOT_W,
      align: 'right' as const,
      onHeaderCell: () => ({ className: styles.wrapHeader }),
      render: (_: unknown, record: Row) => {
        if ((record as any).collapsed) return null;
        return (
          <MeterReadDetailsTableRunningTotalCell
            record={record}
            start={null}
            stop={null}
            fieldsWithRunningTotals={fieldsWithRunningTotals}
            styles={styles}
          />
        );
      },
    },
    {
      title: <ContractTitle contractStart={contractStart} contractEnd={contractEnd} />,

      dataIndex: 'contractYearTotal',
      width: CONTR_W,
      align: 'right' as const,
      onHeaderCell: () => ({ className: styles.wrapHeader }),
      render: (_: unknown, record: Row) => {
        if ((record as any).collapsed) return null;
        // Display N/A if both contract dates are missing
        if (!contractStart && !contractEnd) {
          return <span className={styles.placeholderCell}>N/A</span>;
        }

        return (
          <MeterReadDetailsTableRunningTotalCell
            record={record}
            start={contractStart}
            stop={contractEnd}
            fieldsWithRunningTotals={fieldsWithRunningTotals}
            styles={styles}
          />
        );
      },
    },
    {
      title: 'Energy Bid',
      dataIndex: 'energyBid',
      width: BID_W,
      align: 'right' as const,
      onHeaderCell: () => ({ className: styles.wrapHeader }),
      render: (_: unknown, record: Row) => {
        if ((record as any).collapsed) return null;
        return (
          <span className={styles.placeholderCell}>
            {energyBid === null || energyBid === undefined ? '--' : energyBid.toLocaleString()}
          </span>
        );
      },
    },
  ];

  const isAgcMeter = meterType?.toLowerCase() === 'agc wind farm';

  // Separate children and non-children; also drop AGC rows for non-AGC meters
  const childMap = new Map(rows.filter((r) => NETGEN_CHILDREN.has(r.field)).map((r) => [r.field, r]));
  const children = NETGEN_CHILDREN_ORDER.map((f) => childMap.get(f)).filter(Boolean) as typeof rows;
  const rowMap = new Map(rows.map((r) => [r.field, r]));

  // Build the pre-section block: meter_read_type → prod_stat_netgen → children
  const preSectionFields = ['meter_read_type', NETGEN_PARENT];
  const preSectionRows = preSectionFields.map((f) => rowMap.get(f)).filter(Boolean) as typeof rows;

  // Build the post-section block in defined order, suppressing AGC rows for non-AGC meters
  const postSectionRows = POST_SECTION_ORDER
    .filter((f) => isAgcMeter || !AGC_FIELDS.has(f))
    .map((f) => rowMap.get(f))
    .filter(Boolean) as typeof rows;

  // Rows not covered by either block (e.g. manual_kwh_adjustment, remaining fields)
  const handledFields = new Set([...preSectionFields, NETGEN_PARENT, ...NETGEN_CHILDREN, ...POST_SECTION_ORDER]);
  const remainingRows = rows.filter((r) => !handledFields.has(r.field));

  const reordered = [...preSectionRows, ...children, ...postSectionRows, ...remainingRows];

  const readOnlyRows = reordered
    .filter((row) => !(NETGEN_CHILDREN.has(row.field) && netgenCollapsed))
    .map((row) => ({
      ...row,
      editable: false,
      separatorBelow: row.field === SEPARATOR_FIELD,
      sectionStart: row.field === 'post_prod_stat_netgen',
      subRow: NETGEN_CHILDREN.has(row.field),
      childrenCollapsed: row.field === NETGEN_PARENT ? netgenCollapsed : undefined,
      onToggleCollapse: row.field === NETGEN_PARENT ? toggleNetgen : undefined,
    }));

  return (
    <MeterReadDetailsTable
      id={id}
      rows={readOnlyRows}
      handleChange={handleChange}
      saving={saving}
      updating={updating}
      additionalColumns={totalsCols}
      start={start}
      end={end}
      shouldUseContractView={shouldUseContractView}
      isEditable={isEditable ?? false}
    />
  );
};

export default MeterReadDetailsMainTable;